<template>
	<section :key="authStore.mfa.multifactorToken" class="social-login__guts">
		<template v-if="authStore.mfa.multifactorToken">
			<mfa-login-process
				:invite-code="inviteCode"
			/>
		</template>
		<template v-else>
			<div v-if="signupMsg" class="sz-2 text-center bg-harriet p-3 mb-2">
				<p>{{ signupMsg }}</p>
			</div>

			<template v-if="!showForm && !showCustomTos">
				<external-auth-button
					v-if="!requiredAuthProvider || requiredAuthProvider === 'Google'"
					ref="google"
					:provider="requiredAuthProvider || 'Google'"
					:button-text="buttonPrefix + ' with Google'"
					authentication-action="signup"
					:return-url="$route.query.returnUrl"
					:invite-code="inviteCode"
					:auth-method-type="requiredAuthMethod || 'Google'"
					:organization-name="organizationName"
					:status="authStore.googleSubmitStatus"
					@click.native="toggleGoogleSubmit"
					@authenticated="cognitoSocialSignup($event, 'Google')"
					@cancelled="resetButtonStatuses"
				/>

				<c-callout v-if="showSsoError && authenticationProvider === 'Google'" type="error">
					<p>We couldn't find an email address associated with your Google account.</p>
				</c-callout>

				<external-auth-button
					v-if="!requiredAuthProvider || requiredAuthProvider === 'Facebook'"
					ref="facebook"
					:provider="requiredAuthProvider || 'Facebook'"
					:button-text="buttonPrefix + ' with Facebook'"
					authentication-action="signup"
					:return-url="$route.query.returnUrl"
					:invite-code="inviteCode"
					:auth-method-type="requiredAuthMethod || 'Facebook'"
					:organization-name="organizationName"
					:status="authStore.facebookSubmitStatus"
					@click.native="toggleFacebookSubmit"
					@authenticated="cognitoSocialSignup($event, 'Facebook')"
					@cancelled="resetButtonStatuses"
				/>

				<c-callout v-if="showSsoError && authenticationProvider === 'Facebook'" type="error">
					<p>You need an email address on your Facebook account to sign up this way.</p>
				</c-callout>

				<external-auth-button
					v-if="!requiredAuthProvider || requiredAuthProvider === 'Microsoft'"
					ref="microsoft"
					:provider="requiredAuthProvider || 'Microsoft'"
					:button-text="buttonPrefix + ' with Microsoft'"
					authentication-action="signup"
					:return-url="$route.query.returnUrl"
					:invite-code="inviteCode"
					:auth-method-type="requiredAuthMethod || 'Microsoft'"
					:organization-name="organizationName"
					:status="authStore.microsoftSubmitStatus"
					@click.native="toggleMicrosoftSubmit"
					@authenticated="cognitoSocialSignup($event, 'Microsoft')"
					@cancelled="resetButtonStatuses"
				/>

				<c-callout v-if="showSsoError && authenticationProvider === 'Microsoft'" type="error">
					<p>We couldn't find an email address associated with your Microsoft account.</p>
				</c-callout>
				<slot name="captcha" />

				<external-auth-button
					v-if="requiredAuthProvider === 'OpenIdConnect'"
					ref="openidconnect"
					:provider="requiredAuthProvider || 'OpenIdConnect'"
					:button-text="buttonPrefix + ' with SSO'"
					authentication-action="signup"
					:return-url="$route.query.returnUrl"
					:invite-code="inviteCode"
					:auth-method-type="requiredAuthMethod || 'Custom'"
					:organization-name="organizationName"
					:status="authStore.openIdConnectSubmitStatus"
					use-redirect
					@click.native="toggleOpenIdConnectSubmit"
					@authenticated="cognitoSocialSignup($event, 'OpenIdConnect')"
					@cancelled="resetButtonStatuses"
				/>

				<c-callout v-if="showSsoError && authenticationProvider === 'OpenIdConnect'" type="error">
					<p>We couldn't find an email address associated with your SSO account.</p>
				</c-callout>
				<slot name="captcha" />

				<template v-if="!requiredAuthProvider">
					<div class="strike">
						<span>or</span>
					</div>

					<c-button class="button button--primary bg-deep sz-4" title="Sign Up with Email" @click="toggleEmailSignup">
						{{ buttonPrefix }} with Email
					</c-button>
				</template>
			</template>

			<template v-if="showForm || showCustomTos">
				<ValidationObserver ref="form" v-slot="{ invalid, validated }" tag="form" novalidate class="social-login__form" @keyup="handleKeyUp" @submit.prevent="handleSignup">
					<template v-if="!showCustomTos">
						<c-deprecated-validated-input
							id="name"
							v-model.trim="name"
							type="text"
							placeholder="Name"
							name="Name"
							rules="required"
							autofocus
						/>

						<c-deprecated-validated-input
							id="email-address"
							v-model.trim="email"
							type="email"
							placeholder="Email Address"
							name="Email Address"
							rules="required|email|unique-email"
							@blur="emailChanged"
						>
							<template #error="{ failedRules }">
								<c-callout v-if="failedRules['unique-email']" type="error" class="sz-2">
									<p>An account with this email address already exists. <router-link tag="a" to="/login" title="Log in to Cognito Forms">Please log in</router-link>.</p>
								</c-callout>
							</template>
						</c-deprecated-validated-input>

						<c-create-password-input v-model="password" />
					</template>

					<c-deprecated-validated-input
						id="agree-to-terms"
						v-model="agreeToTerms"
						type="checkbox"
						class="terms-of-use sz-2"
						name="Acceptance of Terms of Service and Privacy Policy"
						rules="accept-terms"
					>
						I agree to the <a href="/legal/terms" target="_blank" title="Agree to Terms of Service">Terms of Service</a> &amp; <a href="/legal/privacy" target="_blank" title="Agree to Privacy Policy">Privacy Policy</a>
					</c-deprecated-validated-input>

					<slot name="captcha" />

					<c-status-button
						class="button button--primary bg-deep sz-4"
						title="Sign up for Cognito Forms" type="submit" :status="authStore.emailSubmitStatus"
						:disabled="validated && invalid"
						@cancelled="resetButtonStatuses"
					>
						{{ buttonPrefix }}
					</c-status-button>
				</ValidationObserver>
			</template>

			<div v-if="!showCustomTos" class="sz-2 text-center">
				<p v-if="inviteCode"><router-link :to="{ name: 'login', query: { 'authenticationProvider': requiredAuthProvider }, params: { requiredAuthMethod }}" title="Log in to Cognito Forms">Log in</router-link> to join with an existing account.</p>
				<p v-else><router-link :to="{ name: 'login', query: { 'authenticationProvider': requiredAuthProvider }, params: { requiredAuthMethod } }" title="Log in to Cognito Forms">Log in</router-link> if you have an account.</p>

				<p v-if="showForm">
					<a href="#" @click="toggleEmailSignup">Sign up with Google, Facebook, or Microsoft.</a>
				</p>
			</div>
		</template>
	</section>
</template>

<script>
	import ExternalAuthButton from 'src/components/auth/ExternalAuthButton.vue';
	import { emailSignup, socialSignup, accountExists } from '@cognitoforms/api/services/authentication';
	import { oAuthUserDetails } from '@cognitoforms/api/services/integration';
	import CButton from '@cognitoforms/shared-components/src/components/interaction/Button.vue';
	import CCallout from '@cognitoforms/shared-components/src/components/content/Callout.vue';
	import CCreatePasswordInput from 'src/components/auth/CreatePasswordInput.vue';
	import CStatusButton from '@cognitoforms/shared-components/src/components/interaction/StatusButton.vue';
	import CDeprecatedValidatedInput from '@cognitoforms/shared-components/src/components/form-controls/DeprecatedValidatedInput.vue';
	import { ValidationObserver, extend } from 'vee-validate';
	import { enableBotProtection } from '@cognitoforms/api/services/service-request';
	import { UserType } from '@cognitoforms/api/services/bot-protection';
	import removeNonAlphaNumeric from 'src/utilities/removeNonAlphaNumeric';
	import { authStore } from 'src/stores/auth-store';
	import MfaLoginProcess from 'src/components/auth/MfaLoginProcess.vue';
	import generateAuthorizeUrl from 'src/components/auth/open-id-connect.ts';

	let isHuman = false;

	extend('unique-email', {
		validate: async email => {
			return isHuman ? !(await accountExists(encodeURIComponent(email))) : true;
		},
		message: 'An account with this email address already exists.',
		immediate: false
	});

	extend('accept-terms', {
		validate: value => { return !!value; },
		message: 'You must agree to the Terms of Service and Privacy Policy.'
	});

	export default {
		name: 'Signup',
		components: {
			ExternalAuthButton,
			CButton,
			CCallout,
			CCreatePasswordInput,
			CStatusButton,
			CDeprecatedValidatedInput,
			ValidationObserver,
			MfaLoginProcess
		},
		props: {
			invitation: { type: Object, default: null },
			prefilledEmail: { type: String, default: '' },
			signupMsg: { type: String, default: null },
			apiConnection: { type: Object, default: null },
			requiredAuthMethod: { type: [String, Number], default: null },
			social: { type: Boolean, default: false },
			autoSignupProvider: { type: String, default: null }
		},
		data() {
			return {
				name: '',
				email: this.prefilledEmail || this.invitation.InvitedEmail || '',
				password: '',
				agreeToTerms: false,
				showForm: !!this.prefilledEmail || false,
				authenticationProvider: this.$route.query.authenticationProvider,
				showSsoError: false,
				internalRequiredAuthMethod: this.requiredAuthMethod ? this.requiredAuthMethod.toString() : null,
				authStore,
				mfaLoading: false,
				returnUrl: this.$route.query.returnUrl ? this.$route.query.returnUrl + (this.$route.hash || '') : null,
				organizationName: this.$route.query.organizationName,
				encryptedUserInfo: this.$route.query.encryptedUserInfo,
				encryptedState: this.$route.query.encryptedState
			};
		},
		head() {
			return {
				title: 'Sign Up',
				meta: [
					{
						name: 'description',
						content: 'Cognito Forms, a free online form builder that helps you collect information and payments. Easily create feedback forms, payment forms, registration forms, and much more.'
					}
				]
			};
		},
		computed: {
			showCustomTos() {
				return this.requiredAuthMethod === 'Custom' && this.requiredAuthProvider === 'OpenIdConnect' && this.inviteCode;
			},
			inviteCode() {
				return this.invitation ? this.invitation.inviteCode : null;
			},
			verificationCode() {
				return this.invitation ? this.invitation.verificationCode : null;
			},
			buttonPrefix() {
				return this.showCustomTos ? 'Continue' : !this.inviteCode ? 'Sign Up' : 'Join';
			},
			requiredAuthProvider() {
				if (this.requiredAuthMethod && this.requiredAuthMethod !== 'Unknown') {
					return this.$route.query.authenticationProvider;
				}
				return null;
			}
		},
		watch: {
			name () {
				const linkExpression = /(?:(http|ftp|https|file|localhost):\/\/)?([\w_-]+(?:(?:\.[\w_-]+)+))([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])/g;
				const linkRegExp = new RegExp(linkExpression, 'i');

				// Immediately remove links from name if detected
				if (linkRegExp.test(this.name)) {
					const newName = this.name.replace(linkExpression, ' ');
					this.name = newName;
				}
			}
		},
		created() {
			enableBotProtection()
				.then(bp => bp.whenActivityDetected(bp => (isHuman = bp.userType === UserType.Human)));

			if (this.apiConnection.userCode)
				this.getOAuthUserInformation();

			if (this.social)
				this.showForm = false;
		},
		mounted() {
			if (this.autoSignupProvider) {
				this.autoSignup();
			}
		},
		methods: {
			autoSignup() {
				const authButton = this.$refs[this.autoSignupProvider.toLowerCase()];
				authButton.openDialog();
			},
			emailChanged() {
				this.$emit('emailCaptured', this.email);
			},
			toggleGoogleSubmit() {
				this.authStore.googleSubmitStatus = 'validating';
			},
			toggleFacebookSubmit() {
				this.authStore.facebookSubmitStatus = 'validating';
			},
			toggleMicrosoftSubmit() {
				this.authStore.microsoftSubmitStatus = 'validating';
			},
			toggleOpenIdConnectSubmit() {
				this.authStore.openIdConnectSubmitStatus = 'validating';
			},
			resetButtonStatuses() {
				this.authStore.emailSubmitStatus = this.authStore.googleSubmitStatus = this.authStore.facebookSubmitStatus = this.authStore.microsoftSubmitStatus = this.authStore.openIdConnectSubmitStatus = 'default';
			},
			toggleEmailSignup() {
				this.showForm = !this.showForm;
			},
			handleSignup() {
				return this.showCustomTos ? this.openIdConnectSignup() : this.cognitoSignup();
			},
			async cognitoSignup() {
				this.showSsoError = false;
				if (this.authStore.emailSubmitStatus === 'validating')
					return;

				this.authStore.emailSubmitStatus = 'validating';

				if (await this.$refs.form.validate()) {
					emailSignup(this.email, this.password, this.name, this.organizationName, this.agreeToTerms, this.inviteCode, this.verificationCode)
						.then(res => {
							if (res.Succeeded)
								return window.location.assign(res.ReturnUrl);

							this.authStore.mfa.multifactorToken = res.MfaToken;
							this.authStore.mfa.encryptedAuthMethod = res.EncryptedAuthMethod;

							if (res.RequiredAuthMethod) {
								this.internalRequiredAuthMethod = res.RequiredAuthMethod;
							}

							this.resetButtonStatuses();
						})
						.catch(e => {
							console.warn(e);
							this.resetButtonStatuses();
						});
				}
				else {
					this.resetButtonStatuses();
				}
			},
			cognitoSocialSignup(token, provider) {
				this.showSsoError = false;
				this.authenticationProvider = '';
				const deviceToken = localStorage.getItem('deviceToken');

				socialSignup(provider, token, this.inviteCode, this.organizationName, deviceToken)
					.then(res => {
						if (res.Succeeded)
							return window.location.assign(res.ReturnUrl);
						else
							this.showSsoError = true;

						if (res.MfaToken) {
							this.authStore.mfa.isSocial = true;
							this.authStore.mfa.multifactorToken = res.MfaToken;
							this.authStore.mfa.backupEmail = res.MfaBackupEmail;
							this.authStore.mfa.encryptedAuthMethod = res.EncryptedAuthMethod;
							this.authenticationProvider = provider;
							return;
						}

						this.authStore.mfa.isSocial = true;
						this.authStore.mfa.multifactorToken = res.MfaToken;
						this.authStore.mfa.encryptedAuthMethod = res.EncryptedAuthMethod;
						this.authenticationProvider = provider;

						this.resetButtonStatuses();
					}).catch(err => {
						console.error(`social signup error: ${err}`);
						this.showSsoError = true;
						this.resetButtonStatuses();
					});
			},
			async openIdConnectSignup() {
				if (await this.$refs.form.validate()) {
					const url = generateAuthorizeUrl('invite-signup', 'redirect', {
						encryptedUserInfo: this.encryptedUserInfo,
						encryptedState: this.encryptedState
					});

					window.location.assign(url);
				}
			},
			handleKeyUp(e) {
				// Auto-stripping name as user types it
				if (e.target.id === 'name')
					this.name = removeNonAlphaNumeric(this.name);
			},
			getOAuthUserInformation() {
				this.showCognitoDisclaimers = true;
				oAuthUserDetails(this.apiConnection.userCode)
					.then(res => {
						this.email = res.Email;
						this.name = res.Name;
						this.organizationName = res.Organization;
					});
			}
		}
	};
</script>